import { useEffect, useMemo, useState } from "react";
import {
  Card,
  Col,
  Row,
  Select,
  Space,
  Statistic,
  Typography,
  Button,
  Divider,
  Empty,
} from "antd";
import { useQuery } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import {
  BookOutlined,
  VideoCameraOutlined,
  DashboardOutlined,
  PlusOutlined,
  FileTextOutlined,
  LineChartOutlined,
} from "@ant-design/icons";
import { Tv } from "lucide-react";
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from "recharts";
import { myLibraryApi } from "../services/api";
import { usePermissions } from "../hooks/usePermissions";

const { Title } = Typography;

const COLORS = {
  total: "#722ed1",
  books: "#52c41a",
  movies: "#1677ff",
  series: "#faad14",
};

function formatMinutes(totalMinutes: number) {
  const m = Math.max(0, Math.floor(totalMinutes || 0));
  const h = Math.floor(m / 60);
  const mm = m % 60;
  if (h <= 0) {
    return `${mm} min`;
  }
  return `${h} godz ${mm} min`;
}

const MONTH_NAMES = [
  "Sty",
  "Lut",
  "Mar",
  "Kwi",
  "Maj",
  "Cze",
  "Lip",
  "Sie",
  "Wrz",
  "Paź",
  "Lis",
  "Gru",
];

const FULL_MONTH_NAMES = [
  "Styczeń",
  "Luty",
  "Marzec",
  "Kwiecień",
  "Maj",
  "Czerwiec",
  "Lipiec",
  "Sierpień",
  "Wrzesień",
  "Październik",
  "Listopad",
  "Grudzień",
];

function CustomChart({
  values,
  color,
  height = 180,
}: {
  values: number[];
  color: string;
  height?: number;
}) {
  const data = useMemo(() => {
    return values.map((v, i) => ({
      name: MONTH_NAMES[i] || `${i + 1}`,
      value: v,
    }));
  }, [values]);

  const gradientId = `gradient-${color.replace("#", "")}`;

  return (
    <div style={{ width: "100%", height: height }}>
      <ResponsiveContainer width="100%" height="100%">
        <AreaChart
          data={data}
          margin={{ top: 10, right: 10, left: -20, bottom: 0 }}
        >
          <defs>
            <linearGradient id={gradientId} x1="0" y1="0" x2="0" y2="1">
              <stop offset="5%" stopColor={color} stopOpacity={0.3} />
              <stop offset="95%" stopColor={color} stopOpacity={0} />
            </linearGradient>
          </defs>
          <CartesianGrid
            strokeDasharray="3 3"
            vertical={false}
            stroke="#f0f0f0"
          />
          <XAxis
            dataKey="name"
            axisLine={false}
            tickLine={false}
            tick={{ fontSize: 12, fill: "#8c8c8c" }}
          />
          <YAxis
            hide={false}
            axisLine={false}
            tickLine={false}
            tick={{ fontSize: 12, fill: "#8c8c8c" }}
          />
          <Tooltip
            contentStyle={{
              borderRadius: 8,
              border: "none",
              boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
              padding: "8px 12px",
            }}
            itemStyle={{ fontWeight: 600 }}
          />
          <Area
            type="monotone"
            dataKey="value"
            stroke={color}
            strokeWidth={3}
            fillOpacity={1}
            fill={`url(#${gradientId})`}
            dot={{ r: 4, fill: color, strokeWidth: 2, stroke: "#fff" }}
            activeDot={{ r: 6, strokeWidth: 0 }}
          />
        </AreaChart>
      </ResponsiveContainer>
    </div>
  );
}

export function MyLibraryDashboardPage() {
  const navigate = useNavigate();
  const currentYear = new Date().getFullYear();

  const { data: yearsData, isLoading: yearsLoading } = useQuery({
    queryKey: ["my-library-years"],
    queryFn: myLibraryApi.listYears,
  });

  const years = yearsData?.years ?? [];

  const [year, setYear] = useState<number>(currentYear);
  const [month, setMonth] = useState<number | undefined>(undefined);

  useEffect(() => {
    if (years.length > 0 && !years.includes(year)) {
      setYear(years[0]);
    }
  }, [years, year]);

  const { canCreate } = usePermissions();
  const yearOptions = useMemo(() => {
    const set = new Set<number>([...years, currentYear]);
    return Array.from(set).sort((a, b) => b - a);
  }, [years, currentYear]);

  const monthOptions = useMemo(
    () => [
      { value: 0, label: "Cały rok" },
      ...Array.from({ length: 12 }, (_, i) => ({
        value: i + 1,
        label: FULL_MONTH_NAMES[i],
      })),
    ],
    [],
  );

  const { data: stats, isLoading: statsLoading } = useQuery({
    queryKey: ["my-library-stats", year, month],
    queryFn: () => myLibraryApi.getStats({ year, month }),
  });

  const { data: monthly, isLoading: monthlyLoading } = useQuery({
    queryKey: ["my-library-monthly", year],
    queryFn: () => myLibraryApi.getMonthly(year),
  });

  const monthlyItems =
    monthly?.items ??
    Array.from({ length: 12 }, (_, i) => ({
      month: i + 1,
      total_completed: 0,
      books_completed: 0,
      movies_completed: 0,
      series_completed: 0,
      pages_read: 0,
      minutes_watched_movies: 0,
      minutes_watched_series: 0,
      minutes_watched: 0,
    }));
  const monthlyMax = Math.max(
    ...monthlyItems.map((x) => x.total_completed || 0),
    1,
  );

  const monthlyTotalSeries = monthlyItems.map((m) => m.total_completed || 0);
  const monthlyBooksSeries = monthlyItems.map((m) => m.books_completed || 0);
  const monthlyMoviesSeries = monthlyItems.map((m) => m.movies_completed || 0);
  const monthlySeriesSeries = monthlyItems.map((m) => m.series_completed || 0);

  const monthlyPagesSeries = monthlyItems.map((m) => m.pages_read || 0);
  const monthlyMoviesMinutesSeries = monthlyItems.map(
    (m) => m.minutes_watched_movies || 0,
  );
  const monthlySeriesMinutesSeries = monthlyItems.map(
    (m) => m.minutes_watched_series || 0,
  );

  return (
    <div>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "flex-start",
          gap: 16,
          marginBottom: 24,
        }}
      >
        <div>
          <Title level={2} style={{ margin: 0 }}>
            Moja biblioteka
          </Title>
          <div style={{ marginTop: 8 }}>
            <Space>
              <span>Rok:</span>
              <Select
                style={{ width: 100 }}
                loading={yearsLoading}
                value={year}
                onChange={setYear}
                options={yearOptions.map((y) => ({ value: y, label: y }))}
              />
              <span>Miesiąc:</span>
              <Select
                style={{ width: 120 }}
                value={month ?? 0}
                onChange={(v) => setMonth(v === 0 ? undefined : v)}
                options={monthOptions}
              />
            </Space>
          </div>
        </div>

        <Button
          type="primary"
          icon={<PlusOutlined />}
          onClick={() => navigate("/my-library/add")}
          disabled={!canCreate}
          title={!canCreate ? "Brak uprawnień do dodawania" : undefined}
        >
          Dodaj wpis
        </Button>
      </div>

      <Row gutter={[16, 16]}>
        <Col xs={24} sm={12} lg={6}>
          <Card
            loading={statsLoading}
            onClick={() => navigate(`/my-library/entries?year=${year}`)}
            style={{ cursor: "pointer" }}
            hoverable
          >
            <Statistic
              title="Łącznie ukończonych"
              value={stats?.total_completed ?? 0}
              prefix={<DashboardOutlined style={{ color: COLORS.total, marginRight: 8 }} />}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card
            loading={statsLoading}
            onClick={() => navigate(`/my-library/entries?year=${year}&type=book`)}
            style={{ cursor: "pointer" }}
            hoverable
          >
            <Statistic
              title="Książki"
              value={stats?.books_completed ?? 0}
              prefix={<BookOutlined style={{ color: COLORS.books, marginRight: 8 }} />}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card
            loading={statsLoading}
            onClick={() => navigate(`/my-library/entries?year=${year}&type=movie`)}
            style={{ cursor: "pointer" }}
            hoverable
          >
            <Statistic
              title="Filmy"
              value={stats?.movies_completed ?? 0}
              prefix={<VideoCameraOutlined style={{ color: COLORS.movies, marginRight: 8 }} />}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card
            loading={statsLoading}
            onClick={() => navigate(`/my-library/entries?year=${year}&type=series`)}
            style={{ cursor: "pointer" }}
            hoverable
          >
            <Statistic
              title="Seriale (sezony)"
              value={stats?.series_completed ?? 0}
              prefix={<Tv size="1em" style={{ color: COLORS.series, marginRight: 8 }} />}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card loading={statsLoading}>
            <Statistic
              title="Strony przeczytane"
              value={stats?.pages_read ?? 0}
              suffix={`(${stats?.books_completed ?? 0} książek)`}
              prefix={<FileTextOutlined style={{ color: COLORS.books, marginRight: 8 }} />}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card loading={statsLoading}>
            <Statistic
              title="Czas oglądania - filmy"
              value={formatMinutes(stats?.minutes_watched_movies ?? 0)}
              suffix={`(${stats?.movies_completed ?? 0} filmów)`}
              prefix={<VideoCameraOutlined style={{ color: COLORS.movies, marginRight: 8 }} />}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card loading={statsLoading}>
            <Statistic
              title="Czas oglądania - seriale"
              value={formatMinutes(stats?.minutes_watched_series ?? 0)}
              suffix={`(${stats?.series_completed ?? 0} sezonów)`}
              prefix={<Tv size="1em" style={{ color: COLORS.series, marginRight: 8 }} />}
            />
          </Card>
        </Col>
      </Row>

      <Divider style={{ margin: "48px 0 24px 0" }} />

      <Card style={{ borderRadius: 12, marginBottom: 48 }}>
        <Title level={3} style={{ textAlign: "center", marginBottom: 24 }}>
          {month
            ? `Podsumowanie - ${FULL_MONTH_NAMES[month - 1]} ${year}`
            : `Podsumowanie roku ${year}`}
        </Title>

        <Row gutter={[24, 24]} justify="center">
          <Col xs={24} sm={12} md={8} lg={5}>
            <Card
              style={{
                borderRadius: 16,
                textAlign: "center",
                background: "#f6ffed",
                border: "1px solid #b7eb8f",
                height: "100%",
              }}
            >
              <Statistic
                title="Przeczytane strony"
                value={stats?.pages_read ?? 0}
                valueStyle={{
                  color: COLORS.books,
                  fontSize: 28,
                  fontWeight: "bold",
                }}
                prefix={<BookOutlined style={{ marginRight: 8 }} />}
              />
              <Divider style={{ margin: "12px 0" }} />
              <Typography.Text type="secondary">
                Średnio{" "}
                <strong>{Math.round((stats?.pages_read ?? 0) / 12)}</strong> stron
                / m-c
              </Typography.Text>
            </Card>
          </Col>
          <Col xs={24} sm={12} md={8} lg={5}>
            <Card
              style={{
                borderRadius: 16,
                textAlign: "center",
                background: "#e6f7ff",
                border: "1px solid #91d5ff",
                height: "100%",
              }}
            >
              <Statistic
                title="Czas - Filmy"
                value={stats?.minutes_watched_movies ?? 0}
                valueStyle={{
                  color: COLORS.movies,
                  fontSize: 28,
                  fontWeight: "bold",
                }}
                prefix={<VideoCameraOutlined style={{ marginRight: 8 }} />}
                suffix="min"
              />
              <Divider style={{ margin: "12px 0" }} />
              <Typography.Text type="secondary">
                Czyli{" "}
                <strong>
                  {Math.round((stats?.minutes_watched_movies ?? 0) / 60)}
                </strong>{" "}
                godz. filmów
              </Typography.Text>
            </Card>
          </Col>
          <Col xs={24} sm={12} md={8} lg={5}>
            <Card
              style={{
                borderRadius: 16,
                textAlign: "center",
                background: "#fff7e6",
                border: "1px solid #ffd591",
                height: "100%",
              }}
            >
              <Statistic
                title="Czas - Seriale"
                value={stats?.minutes_watched_series ?? 0}
                valueStyle={{
                  color: COLORS.series,
                  fontSize: 28,
                  fontWeight: "bold",
                }}
                prefix={<Tv size="1em" style={{ marginRight: 8 }} />}
                suffix="min"
              />
              <Divider style={{ margin: "12px 0" }} />
              <Typography.Text type="secondary">
                Czyli{" "}
                <strong>
                  {Math.round((stats?.minutes_watched_series ?? 0) / 60)}
                </strong>{" "}
                godz. seriali
              </Typography.Text>
            </Card>
          </Col>
          <Col xs={24} sm={12} md={8} lg={5}>
            <Card
              style={{
                borderRadius: 16,
                textAlign: "center",
                background: "#f9f0ff",
                border: "1px solid #d3adf7",
                height: "100%",
              }}
            >
              <Statistic
                title="Ukończone dzieła"
                value={stats?.total_completed ?? 0}
                valueStyle={{
                  color: COLORS.total,
                  fontSize: 28,
                  fontWeight: "bold",
                }}
                prefix={<DashboardOutlined style={{ marginRight: 8 }} />}
              />
              <Divider style={{ margin: "12px 0" }} />
              <Typography.Text type="secondary">
                Głównie <strong>{stats?.books_completed ?? 0}</strong> książek
              </Typography.Text>
            </Card>
          </Col>
        </Row>
      </Card>

      {month ? (
        <Card style={{ margin: "48px 0", borderRadius: 12, padding: "24px 0" }}>
          <Empty
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            description={
              <Typography.Text type="secondary" style={{ fontSize: 16 }}>
                Przeglądasz dane dla konkretnego miesiąca. Wykresy trendów rocznych zostały ukryte.
              </Typography.Text>
            }
          >
            <Button type="primary" onClick={() => setMonth(undefined)} style={{ marginTop: 16 }}>
              Pokaż cały rok
            </Button>
          </Empty>
        </Card>
      ) : (
        <>
          <Divider style={{ margin: "24px 0" }} />

          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              marginBottom: 12,
            }}
          >
            <Title level={4} style={{ margin: 0 }}>
              Miesięcznie (Ukończone)
            </Title>
            <Button type="link" onClick={() => navigate("/my-library/entries")}>
              Zobacz wpisy
            </Button>
          </div>

          <Card loading={monthlyLoading} style={{ borderRadius: 12 }}>
            <div
              style={{
                display: "flex",
                justifyContent: "flex-end",
                gap: 12,
                marginBottom: 12,
                flexWrap: "wrap",
              }}
            >
              <span
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 6,
                  color: "#595959",
                }}
              >
                <span
                  style={{
                    width: 10,
                    height: 10,
                    background: COLORS.books,
                    borderRadius: 2,
                    display: "inline-block",
                  }}
                />
                Książki
              </span>
              <span
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 6,
                  color: "#595959",
                }}
              >
                <span
                  style={{
                    width: 10,
                    height: 10,
                    background: COLORS.movies,
                    borderRadius: 2,
                    display: "inline-block",
                  }}
                />
                Filmy
              </span>
              <span
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 6,
                  color: "#595959",
                }}
              >
                <span
                  style={{
                    width: 10,
                    height: 10,
                    background: COLORS.series,
                    borderRadius: 2,
                    display: "inline-block",
                  }}
                />
                Seriale (sezony)
              </span>
            </div>

            <div
              style={{
                display: "flex",
                gap: 8,
                alignItems: "flex-end",
                height: 160,
              }}
            >
              {monthlyItems.map((m) => {
                const total = m.total_completed || 0;
                const barHeight = Math.round((total / monthlyMax) * 120);

                const safeTotal = total > 0 ? total : 1;
                const booksH =
                  total > 0
                    ? Math.max(
                      2,
                      Math.round((m.books_completed / safeTotal) * barHeight),
                    )
                    : 0;
                const moviesH =
                  total > 0
                    ? Math.max(
                      2,
                      Math.round((m.movies_completed / safeTotal) * barHeight),
                    )
                    : 0;
                const seriesH =
                  total > 0
                    ? Math.max(
                      2,
                      Math.round((m.series_completed / safeTotal) * barHeight),
                    )
                    : 0;

                const overflow = Math.max(
                  0,
                  booksH + moviesH + seriesH - barHeight,
                );
                const seriesHFixed = Math.max(0, seriesH - overflow);

                return (
                  <div
                    key={m.month}
                    style={{
                      flex: 1,
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      gap: 6,
                    }}
                  >
                    <div
                      style={{
                        width: "100%",
                        height: barHeight,
                        display: "flex",
                        flexDirection: "column-reverse",
                        borderRadius: 4,
                        overflow: "hidden",
                        background: "#f5f5f5",
                        transition: "height 0.2s",
                      }}
                      title={`${m.month}: ${total} (książki: ${m.books_completed}, filmy: ${m.movies_completed}, seriale: ${m.series_completed})`}
                    >
                      {seriesHFixed > 0 ? (
                        <div
                          style={{
                            height: seriesHFixed,
                            background: COLORS.series,
                          }}
                        />
                      ) : null}
                      {moviesH > 0 ? (
                        <div
                          style={{ height: moviesH, background: COLORS.movies }}
                        />
                      ) : null}
                      {booksH > 0 ? (
                        <div style={{ height: booksH, background: COLORS.books }} />
                      ) : null}
                    </div>
                    <div style={{ fontSize: 12, color: "#888" }}>{m.month}</div>
                  </div>
                );
              })}
            </div>
          </Card>

          <Divider style={{ margin: "24px 0" }} />

          <Title level={4} style={{ margin: 0, marginBottom: 16 }}>
            <LineChartOutlined style={{ marginRight: 8, color: COLORS.total }} />
            Trendy miesięczne (Ilości)
          </Title>

          <Card
            title="Sumarycznie"
            loading={monthlyLoading}
            style={{ marginBottom: 24, borderRadius: 12, overflow: "hidden" }}
          >
            <CustomChart
              values={monthlyTotalSeries}
              color={COLORS.total}
              height={240}
            />
          </Card>

          <Row gutter={[16, 16]}>
            <Col xs={24} md={8}>
              <Card
                title="Książki"
                size="small"
                loading={monthlyLoading}
                style={{ borderRadius: 12 }}
              >
                <CustomChart
                  values={monthlyBooksSeries}
                  color={COLORS.books}
                  height={160}
                />
              </Card>
            </Col>
            <Col xs={24} md={8}>
              <Card
                title="Filmy"
                size="small"
                loading={monthlyLoading}
                style={{ borderRadius: 12 }}
              >
                <CustomChart
                  values={monthlyMoviesSeries}
                  color={COLORS.movies}
                  height={160}
                />
              </Card>
            </Col>
            <Col xs={24} md={8}>
              <Card
                title="Seriale (sezony)"
                size="small"
                loading={monthlyLoading}
                style={{ borderRadius: 12 }}
              >
                <CustomChart
                  values={monthlySeriesSeries}
                  color={COLORS.series}
                  height={160}
                />
              </Card>
            </Col>
          </Row>

          <Divider style={{ margin: "24px 0" }} />

          <Title level={4} style={{ margin: 0, marginBottom: 16 }}>
            <DashboardOutlined style={{ marginRight: 8, color: COLORS.total }} />
            Trendy miesięczne (Konsumpcja)
          </Title>

          <Row gutter={[16, 16]}>
            <Col xs={24} md={8}>
              <Card
                title="Przeczytane strony"
                size="small"
                loading={monthlyLoading}
                style={{ borderRadius: 12 }}
              >
                <CustomChart
                  values={monthlyPagesSeries}
                  color={COLORS.books}
                  height={160}
                />
              </Card>
            </Col>
            <Col xs={24} md={8}>
              <Card
                title="Minuty oglądania (Filmy)"
                size="small"
                loading={monthlyLoading}
                style={{ borderRadius: 12 }}
              >
                <CustomChart
                  values={monthlyMoviesMinutesSeries}
                  color={COLORS.movies}
                  height={160}
                />
              </Card>
            </Col>
            <Col xs={24} md={8}>
              <Card
                title="Minuty oglądania (Seriale)"
                size="small"
                loading={monthlyLoading}
                style={{ borderRadius: 12 }}
              >
                <CustomChart
                  values={monthlySeriesMinutesSeries}
                  color={COLORS.series}
                  height={160}
                />
              </Card>
            </Col>
          </Row>
        </>
      )}
    </div>
  );
}

export default MyLibraryDashboardPage;
